import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import {
    ICustomSeriesPaneRenderer,
    LineStyle,
    PaneRendererCustomData,
    PriceToCoordinateConverter,
    Time
} from 'lightweight-charts'
import { KagiSeriesOptions } from './options'
import { KagiData } from './data';

interface KagiBarItem {
    x: number;
    high: number;
    close: number;
    open: number;
    low: number;
    ltp: number;
    nextCandle: number;
}


export class KagiSeriesRenderer<TData extends KagiData>
    implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<Time, TData> | null = null
    _options: KagiSeriesOptions | null = null

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<Time, TData>,
        options: KagiSeriesOptions
    ): void {
        this._data = data
        this._options = options
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }

        const options = this._options

        const { bars: items, visibleRange, barSpacing: barWidth } = this._data
        const { lineWidth, lineStyle } = this._options
        if (items.length === 0 || visibleRange.from >= items.length || visibleRange.to <= 0) {
            return
        }

        const startIndex = this._data.visibleRange.from;
        const endIndex = this._data.visibleRange.to;

        const endVisible = endIndex === this._data.bars.length;

        const ctx = renderingScope.context

        ctx.lineCap = 'butt'
        ctx.lineWidth = lineWidth * renderingScope.verticalPixelRatio

        setLineStyle(ctx, lineStyle)

        ctx.lineJoin = 'round';
        ctx.beginPath();

        let currentBarItem = items[startIndex];
        let previousBar: KagiBarItem = {
            x: currentBarItem.x * renderingScope.horizontalPixelRatio,
            high: priceToCoordinate(currentBarItem.originalData.high)! * renderingScope.verticalPixelRatio,
            close: priceToCoordinate(currentBarItem.originalData.close)! * renderingScope.verticalPixelRatio,
            open: priceToCoordinate(currentBarItem.originalData.open)! * renderingScope.verticalPixelRatio,
            low: priceToCoordinate(currentBarItem.originalData.low)! * renderingScope.verticalPixelRatio,
            ltp: priceToCoordinate(currentBarItem.originalData.ltp)! * renderingScope.verticalPixelRatio,
            nextCandle: currentBarItem.originalData.nextCandle?.ltp ? priceToCoordinate(currentBarItem.originalData.nextCandle.ltp)! * renderingScope.verticalPixelRatio : 0,
        }

        let currentColor = previousBar.close > previousBar.open ? options.upColor : options.downColor;

        ctx.strokeStyle = currentColor
        ctx.moveTo(previousBar.x - barWidth, previousBar.open);
        ctx.lineTo(previousBar.x, previousBar.open);
        ctx.lineTo(previousBar.x, previousBar.close);
        ctx.stroke();
        
        for (let i= startIndex+1; i < endIndex; i++) {
            currentBarItem = items[i];
            let currentBar: KagiBarItem = {
                x: currentBarItem.x * renderingScope.horizontalPixelRatio,
                high: priceToCoordinate(currentBarItem.originalData.high)! * renderingScope.verticalPixelRatio,
                close: priceToCoordinate(currentBarItem.originalData.close)! * renderingScope.verticalPixelRatio,
                open: priceToCoordinate(currentBarItem.originalData.open)! * renderingScope.verticalPixelRatio,
                low: priceToCoordinate(currentBarItem.originalData.low)! * renderingScope.verticalPixelRatio,
                ltp: currentBarItem.originalData.ltp ? priceToCoordinate(currentBarItem.originalData.ltp)! * renderingScope.verticalPixelRatio : 0,
                nextCandle: currentBarItem.originalData.nextCandle?.ltp ? priceToCoordinate(currentBarItem.originalData.nextCandle.ltp)! * renderingScope.verticalPixelRatio : 0,
            }

            const prevUp = previousBar.close > previousBar.open;

            ctx.lineTo(currentBar.x, previousBar.close);
            if ((prevUp && currentBar.close >= previousBar.open) || (!prevUp && currentBar.close <= previousBar.open)) {
                ctx.lineTo(currentBar.x, currentBar.close);
                ctx.stroke();
            } else {
                ctx.lineTo(currentBar.x, previousBar.open);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(currentBar.x, previousBar.open);
                currentColor = currentBar.open >= currentBar.close ? options.upColor : options.downColor;
                ctx.strokeStyle = currentColor;
                ctx.lineTo(currentBar.x, currentBar.close);
                ctx.stroke();
            }

            if (endVisible && i === endIndex - 1 && currentBar.ltp) {
                
                const {open, close, ltp, nextCandle} = currentBar;

                if ((ltp > close && close > open) || (ltp < close && close < open)){
                    ctx.beginPath();
                    ctx.moveTo(currentBar.x, currentBar.close);
                    ctx.lineTo(currentBar.x, currentBar.ltp)
                    ctx.strokeStyle = currentColor === options.upColor ? options.projectionUpColor : options.projectionDownColor;
                    ctx.stroke();
                } else if (nextCandle) {
                    ctx.beginPath();
                    ctx.moveTo(currentBar.x + barWidth, close);
                    ctx.strokeStyle = currentColor === options.upColor ? options.projectionUpColor : options.projectionDownColor;
                    ctx.lineTo(currentBar.x + barWidth, nextCandle);
                    ctx.stroke();
                }
            }
            previousBar = currentBar;
        }
    }
}

export function setLineStyle(ctx: CanvasRenderingContext2D, style: LineStyle): void {
    const dashPatterns = {
        [LineStyle.Solid]: [],
        [LineStyle.Dotted]: [ctx.lineWidth, ctx.lineWidth],
        [LineStyle.Dashed]: [2 * ctx.lineWidth, 2 * ctx.lineWidth],
        [LineStyle.LargeDashed]: [6 * ctx.lineWidth, 6 * ctx.lineWidth],
        [LineStyle.SparseDotted]: [ctx.lineWidth, 4 * ctx.lineWidth]
    }

    const dashPattern = dashPatterns[style]
    ctx.setLineDash(dashPattern)
}
